// Path assumption: placed at Pages/Outreach/Compensation/InvoiceTemplateEditorPage.tsx,
// same nesting depth as the other 3 compensation components — adjust the relative
// import depths below if you place it somewhere else.
import { ChangeEvent, ReactNode, RefObject, createRef } from "react";
import './compensation-styles.css'
import { InvoiceTemplateType } from "../../../DataTypes/compensation";
import { WorkerUserType } from "../../../DataTypes/activity";
import { ViewItem } from "../../../Components/Views/ViewItem";
import { controllers } from "../../../Controllers/Controllers";
import { MailHtmlFrame, sanitizeEmailHtml } from "../../../Components/Views/MailHtmlFrame";

// Mirrors outreach/compensation_views.py's KNOWN_INVOICE_PLACEHOLDERS. If
// you already export this from compensation.ts, import it from there
// instead of duplicating it here (same reasoning as TemplateFormPage's
// local copy of KNOWN_SIGNATURE_PLACEHOLDERS).
const KNOWN_INVOICE_PLACEHOLDERS = [
    'worker_name', 'worker_email', 'period_start', 'period_end',
    'compensation_date', 'base_salary', 'bonus_rows', 'bonus_total',
    'total_amount', 'notes',
    // Derived from data already on Compensation — not new stored fields
    // (see compensation_views.py's KNOWN_INVOICE_PLACEHOLDERS).
    'compensation_id', 'total_amount_words_en', 'total_amount_words_uk',
] as const

// Sample data for the live preview — same idea as the backend's own
// preview_shared_signature / preview_email_template using "Jane Doe" /
// "Acme Corp" when there's no real lead to render against. Rendered
// entirely client-side (plain string replace, same operation the
// backend does), so the preview updates as you type with no round trip.
const SAMPLE_VALUES: Record<string, string> = {
    worker_name: 'Jane Doe',
    worker_email: 'jane@wyens.co',
    period_start: '2026-09-01',
    period_end: '2026-09-30',
    compensation_date: '2026-10-01',
    base_salary: '2500.00',
    bonus_rows: '<tr><td>Contacted leads bonus</td>'
        + '<td style="text-align:right;">$12.50</td></tr>',
    bonus_total: '12.50',
    total_amount: '2512.50',
    notes: 'Sample notes appear here if any were entered.',
    compensation_id: '482',
    total_amount_words_en: 'Two thousand five hundred twelve US Dollars 50 cents',
    total_amount_words_uk: "Дві тисячі п'ятсот дванадцять доларів США 50 центів",
}

const renderSamplePreview = (html: string): string => {
    let rendered = html
    Object.entries(SAMPLE_VALUES).forEach(([token, value]) => {
        rendered = rendered.split(`{{${token}}}`).join(value)
    })
    return rendered
}

const findUnrecognizedPlaceholders = (html: string): string[] => {
    const found = new Set(Array.from(html.matchAll(/\{\{([a-zA-Z0-9_]+)\}\}/g)).map(m => m[1]))
    const known = new Set<string>(KNOWN_INVOICE_PLACEHOLDERS)
    return Array.from(found).filter(token => !known.has(token))
}

type InvoiceTemplateEditorPageState = {
    templates: InvoiceTemplateType[];
    workers: WorkerUserType[];
    loading: boolean;
    error: string | null;
    needsAuth: boolean;

    // 'default' or a worker id (as a string, to double as a <select> value)
    selectedScope: string;
    name: string;
    html: string;

    saving: boolean;
    saveError: string | null;
    saveMessage: string | null;
}

class InvoiceTemplateEditorPage extends ViewItem {
    state: InvoiceTemplateEditorPageState;
    htmlRef: RefObject<HTMLTextAreaElement | null> = createRef();
    fileInputRef: RefObject<HTMLInputElement | null> = createRef();

    constructor(props: any) {
        super(props)
        this.state = {
            templates: [],
            workers: [],
            loading: true,
            error: null,
            needsAuth: false,

            selectedScope: 'default',
            name: '',
            html: '',

            saving: false,
            saveError: null,
            saveMessage: null,
        }
    }

    componentDidMount(): void {
        this.loadAll()
    }

    loadAll = async () => {
        this.setState({ loading: true, error: null, needsAuth: false })
        if (!controllers().auth.userToken) {
            this.setState({ loading: false, needsAuth: true })
            return
        }

        const [templatesResponse, workersResponse] = await Promise.all([
            controllers().compensation.getInvoiceTemplates(),
            controllers().outreach.getWorkers(),
        ])

        if (!templatesResponse || templatesResponse.statusCode !== 200) {
            this.setState({ loading: false, error: templatesResponse?.error || 'Failed to load invoice templates.' })
            return
        }

        const templates: InvoiceTemplateType[] = templatesResponse.data?.templates || []
        const workers: WorkerUserType[] = (workersResponse && workersResponse.statusCode === 200)
            ? (workersResponse.data?.workers || [])
            : []

        this.setState({ loading: false, templates, workers })
        this.selectScope('default', templates)
    }

    // Re-fetches just the templates list after a save (so "has override"
    // badges and the shared-default row reflect what was just written),
    // without re-fetching the worker list too.
    loadTemplates = async () => {
        const response = await controllers().compensation.getInvoiceTemplates()
        if (response && response.statusCode === 200) {
            this.setState({ templates: response.data?.templates || [] })
            return response.data?.templates || []
        }
        return this.state.templates
    }

    selectScope = (scope: string, templatesOverride?: InvoiceTemplateType[]) => {
        const templates = templatesOverride ?? this.state.templates
        const template = scope === 'default'
            ? templates.find(t => t.is_default)
            : templates.find(t => t.user_id === Number(scope))

        this.setState({
            selectedScope: scope,
            name: template?.name || '',
            html: template?.html || '',
            saveError: null,
            saveMessage: null,
        })
    }

    handleScopeSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
        if (e.target.value) this.selectScope(e.target.value)
    }

    handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        this.setState({ name: e.target.value, saveMessage: null })
    }

    handleHtmlChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ html: e.target.value, saveMessage: null })
    }

    // Insert-at-cursor into the HTML textarea, then restore focus + cursor
    // position right after — same pattern as TemplateFormPage's
    // insertSignaturePlaceholder.
    insertPlaceholder = (token: string) => {
        const el = this.htmlRef.current
        const placeholder = `{{${token}}}`
        const current = this.state.html

        if (!el) {
            this.setState({ html: `${current}${placeholder}` })
            return
        }

        const start = el.selectionStart ?? current.length
        const end = el.selectionEnd ?? current.length
        const next = current.slice(0, start) + placeholder + current.slice(end)
        this.setState({ html: next })

        requestAnimationFrame(() => {
            el.focus()
            const cursor = start + placeholder.length
            el.setSelectionRange(cursor, cursor)
        })
    }

    handleUploadClick = () => {
        this.fileInputRef.current?.click()
    }

    handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return

        const reader = new FileReader()
        reader.onload = () => {
            const content = typeof reader.result === 'string' ? reader.result : ''
            this.setState({ html: content, saveMessage: null })
        }
        reader.readAsText(file)
    }

    handleSave = async () => {
        const { selectedScope, name, html } = this.state
        this.setState({ saving: true, saveError: null, saveMessage: null })

        const response = await controllers().compensation.saveInvoiceTemplate({
            user_id: selectedScope === 'default' ? null : Number(selectedScope),
            name,
            html,
        })

        if (!response || response.statusCode !== 200) {
            this.setState({ saving: false, saveError: response?.error || 'Failed to save template.' })
            return
        }

        const templates = await this.loadTemplates()
        this.setState({ saving: false, saveMessage: 'Template saved.' })
        // Re-select the same scope so `templates` (used for the list's
        // "Custom" badges) picks up the row we just wrote.
        this.selectScope(selectedScope, templates)
    }

    renderTemplateList = () => {
        const { templates, workers, selectedScope } = this.state
        const defaultTemplate = templates.find(t => t.is_default)
        const overrideByUserId: Record<number, InvoiceTemplateType> = {}
        templates.forEach(t => { if (t.user_id) overrideByUserId[t.user_id] = t })

        const workersWithOverride = workers.filter(w => overrideByUserId[w.id])
        const workersWithoutOverride = workers.filter(w => !overrideByUserId[w.id])

        return (
            <div className="lead-form-section">
                <h3 className="lead-form-section-title">Templates</h3>
                <p className="outreach-muted">
                    Every worker's invoice uses their own override if one exists, otherwise the shared default.
                </p>

                <div className="comp-template-list">
                    <button
                        type="button"
                        className={`comp-template-list-item ${selectedScope === 'default' ? 'comp-template-list-item-active' : ''}`}
                        onClick={() => this.selectScope('default')}
                    >
                        <span>Shared default</span>
                        {!defaultTemplate && <span className="comp-template-list-empty">Not set up yet</span>}
                    </button>

                    {workersWithOverride.map(w => (
                        <button
                            type="button"
                            key={w.id}
                            className={`comp-template-list-item ${selectedScope === String(w.id) ? 'comp-template-list-item-active' : ''}`}
                            onClick={() => this.selectScope(String(w.id))}
                        >
                            <span>{w.username || w.email}</span>
                            <span className="comp-template-list-badge">Custom</span>
                        </button>
                    ))}
                </div>

                {workersWithoutOverride.length > 0 && (
                    <div className="category-form-group comp-template-add-override">
                        <label className="category-form-label">Add an override for...</label>
                        <select className="category-form-input" value="" onChange={this.handleScopeSelectChange}>
                            <option value="">Select a worker...</option>
                            {workersWithoutOverride.map(w => (
                                <option key={w.id} value={w.id}>{w.username || w.email}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>
        )
    }

    renderEditor = () => {
        const { selectedScope, name, html, saving } = this.state
        const unrecognized = findUnrecognizedPlaceholders(html)
        const isDefault = selectedScope === 'default'

        return (
            <div className="lead-form-section">
                <h3 className="lead-form-section-title">
                    {isDefault ? 'Shared default template' : 'Override for this worker'}
                </h3>

                {!isDefault && (
                    <p className="outreach-muted">
                        This only affects invoices for this one worker. Leave the HTML blank and save to
                        remove the override — their invoices will fall back to the shared default.
                    </p>
                )}

                {unrecognized.length > 0 && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">
                            Unrecognized placeholder{unrecognized.length === 1 ? '' : 's'}: {unrecognized.map(t => `{{${t}}}`).join(', ')} — these won't be replaced when sent.
                        </span>
                    </div>
                )}

                <div className="category-form-group">
                    <label className="category-form-label">Name (for your own reference)</label>
                    <input
                        className="category-form-input"
                        value={name}
                        onChange={this.handleNameChange}
                        placeholder="e.g. Standard monthly invoice"
                        disabled={saving}
                    />
                </div>

                <div className="template-placeholder-bar">
                    <span className="template-placeholder-label">Insert placeholder:</span>
                    <div className="template-placeholder-chips">
                        {KNOWN_INVOICE_PLACEHOLDERS.map(token => (
                            <button
                                key={token}
                                type="button"
                                className="template-placeholder-chip"
                                onClick={() => this.insertPlaceholder(token)}
                            >
                                {`{{${token}}}`}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="category-form-group">
                    <div className="settings-signature-label-row">
                        <label className="category-form-label">Invoice HTML</label>
                        <button type="button" className="settings-signature-upload-btn" onClick={this.handleUploadClick} disabled={saving}>
                            Upload HTML file...
                        </button>
                        <input
                            ref={this.fileInputRef}
                            type="file"
                            accept=".html,.htm,text/html"
                            className="settings-signature-file-input"
                            onChange={this.handleFileChange}
                        />
                    </div>
                    <textarea
                        ref={this.htmlRef}
                        className="category-form-textarea template-html-textarea"
                        value={html}
                        onChange={this.handleHtmlChange}
                        placeholder="Leave blank to use a plain built-in layout"
                        rows={16}
                        disabled={saving}
                    />
                </div>
            </div>
        )
    }

    renderPreview = () => {
        const { html } = this.state
        const rendered = renderSamplePreview(html || '')

        return (
            <div className="lead-form-section template-preview-panel comp-invoice-preview-panel">
                <h3 className="lead-form-section-title">Preview</h3>
                <p className="outreach-muted template-preview-empty">
                    Rendered with sample data — updates as you type.
                </p>
                {html ? (
                    <div className="settings-signature-preview">
                        <MailHtmlFrame html={sanitizeEmailHtml(rendered)} />
                    </div>
                ) : (
                    <p className="outreach-muted">No custom HTML — the built-in layout will be used.</p>
                )}
            </div>
        )
    }

    render(): ReactNode {
        const { loading, error, needsAuth, saving, saveError, saveMessage } = this.state

        return (
            <div className="outreach-page">
                <div className="outreach-header">
                    <div className="outreach-title-header">
                        <h1 className="outreach-title">Invoice templates</h1>
                        <p className="outreach-subtitle">Edit the shared default, or set up a different invoice for a specific worker.</p>
                    </div>
                </div>

                {!loading && needsAuth && (
                    <div className="outreach-banner outreach-banner-info">
                        <span className="outreach-banner-text">Sign in to manage invoice templates.</span>
                        <button className="outreach-banner-action" onClick={() => controllers().modals.authModal.show()}>Sign In</button>
                    </div>
                )}

                {!loading && error && (
                    <div className="outreach-banner outreach-banner-error">
                        <span className="outreach-banner-text">{error}</span>
                        <button className="outreach-banner-action" onClick={this.loadAll}>Retry</button>
                    </div>
                )}

                {loading && <div className="outreach-state"><div className="outreach-spinner" /></div>}

                {!loading && !error && !needsAuth && (
                    <div className="lead-form template-form">
                        {saveError && <div className="category-form-error">{saveError}</div>}
                        {saveMessage && (
                            <div className="outreach-banner outreach-banner-info">
                                <span className="outreach-banner-text">{saveMessage}</span>
                            </div>
                        )}

                        {this.renderTemplateList()}

                        <div className="template-form-columns">
                            <div className="template-form-main-col">
                                {this.renderEditor()}
                                <div className="lead-form-actions">
                                    <button className="outreach-add-btn" onClick={this.handleSave} disabled={saving}>
                                        {saving ? 'Saving...' : 'Save template'}
                                    </button>
                                </div>
                            </div>
                            <div className="template-form-preview-col">
                                {this.renderPreview()}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }
}

export { InvoiceTemplateEditorPage }